/**
 * useLoad: one block's read on the Home and Records tabs (plan
 * docs/plans/lms-plan.md 7.1, 7.5; WP-41), with its own loading and error
 * state so one route failing never blanks the whole page (G10: the failure is
 * shown with Retry, never a silent empty list).
 *
 * The read runs again when the site changes (every /api/lms call is
 * site-scoped, 4.9; an answer for the previous site is dropped), when
 * `reloadKey` changes (the page's refresh) and on reload(). setData lets an
 * action update the block in place (a certificate retried, a row marked).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiErrorMessage } from '../../api/client';
import { useLms } from '../../LmsProvider';

export interface Loaded<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
  setData: (update: (prev: T | null) => T | null) => void;
}

interface LoadState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

export function useLoad<T>(loader: () => Promise<T>, errorText: string, reloadKey = 0): Loaded<T> {
  const { siteId } = useLms();
  const [state, setState] = useState<LoadState<T>>({ data: null, loading: true, error: null });
  const [tick, setTick] = useState(0);
  // The latest loader, so a caller may pass an inline function without re-running the read each render.
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    loaderRef.current().then(
      (data) => {
        if (!cancelled) setState({ data, loading: false, error: null });
      },
      (err: unknown) => {
        if (!cancelled) setState({ data: null, loading: false, error: apiErrorMessage(err, errorText) });
      }
    );
    return () => {
      cancelled = true;
    };
  }, [siteId, tick, reloadKey, errorText]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  const setData = useCallback((update: (prev: T | null) => T | null) => {
    setState((s) => ({ ...s, data: update(s.data) }));
  }, []);

  return { data: state.data, loading: state.loading, error: state.error, reload, setData };
}

export default useLoad;
